import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type { ExperimentalQueries } from '../__types__/ExperimentalQueries.ts';
import { fetchJSON } from '../fetchJSON.ts';
import { MediaType } from '../MediaType.ts';
import type { ResolverContext } from '../resolver-context.ts';

import { ConsumerSpaceQuery } from './ConsumerSpaceQuery.graphql.ts';
import { ExcerptIncludeTargetPageByIdQuery } from './ExcerptIncludeTargetPageByIdQuery.graphql.ts';
import { ExcerptIncludeTargetPageQuery } from './ExcerptIncludeTargetPageQuery.graphql.ts';

import type {
	ConsumerSpaceQuery$data,
	ConsumerSpaceQuery$variables,
} from './__generated__/ConsumerSpaceQuery.graphql.ts';
import type {
	ExcerptIncludeTargetPageByIdQuery$data,
	ExcerptIncludeTargetPageByIdQuery$variables,
} from './__generated__/ExcerptIncludeTargetPageByIdQuery.graphql.ts';
import type {
	ExcerptIncludeTargetPageQuery$data,
	ExcerptIncludeTargetPageQuery$variables,
} from './__generated__/ExcerptIncludeTargetPageQuery.graphql.ts';

const queryGraphQL = (
	network: ResolverContext['network'],
	{ query, variables }: { query: string; variables: any },
) =>
	fetchJSON(network, '/cgraphql', {
		method: 'POST',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: JSON.stringify({
			variables,
			query,
		}),
	});

/**
 * Recursively parses an ADF and returns all nodes that match a given extension
 * key.
 *
 * @param extensionKey The extension key to search for (e.g. 'excerpt-include')
 * @param currentNode The ADF node to recursively search through
 */
const parseForTargetMacroNodeByExtensionKey = (extensionKey: string, currentNode: any): any[] => {
	const matchedNodes: any[] = [];

	if (currentNode?.attrs?.extensionKey === extensionKey) {
		matchedNodes.push(currentNode);
	}

	// Search in direct content children
	if (currentNode.content) {
		for (const childNode of currentNode.content) {
			matchedNodes.push(...parseForTargetMacroNodeByExtensionKey(extensionKey, childNode));
		}
	}

	// Also search inside Legacy Content Macro (LCM) nestedContent parameter
	if (
		currentNode?.attrs?.extensionKey === 'legacy-content' &&
		currentNode?.attrs?.extensionType === 'com.atlassian.confluence.migration' &&
		currentNode?.attrs?.parameters?.nestedContent &&
		fg('confluence_excerpt_include_search_for_lcm')
	) {
		matchedNodes.push(
			...parseForTargetMacroNodeByExtensionKey(
				extensionKey,
				currentNode.attrs.parameters.nestedContent,
			),
		);
	}

	return matchedNodes;
};

/**
 * Queries for content with a given title, space, and type.
 * When the AGG migration experiment is enabled, uses queryInterop (AGG).
 * Otherwise falls back to the legacy cc-graphql endpoint.
 */
const queryContent = (
	network: ResolverContext['network'],
	contentTitle: string,
	spaceKey: string,
	type: 'page' | 'blogpost',
) => {
	if (expValEquals('cc_migrate_experimental_macro_metadata_agg', 'isEnabled', true)) {
		return queryInterop<
			ExcerptIncludeTargetPageQuery$data,
			ExcerptIncludeTargetPageQuery$variables
		>({
			query: ExcerptIncludeTargetPageQuery,
			variables: { contentTitle, spaceKey, type },
			interopApolloAggClient: true,
		}).then((result) => ({ data: result.data }));
	}
	return queryGraphQL(network, {
		query: `
      query excerptIncludeTargetPageQuery(
        $contentTitle: String!
        $spaceKey: String!
        $type: String!
      ) {
        content(title: $contentTitle, spaceKey: $spaceKey, type: $type) {
          nodes {
            id
            body {
              atlas_doc_format {
                value
              }
            }
          }
        }
      }
    `,
		variables: { contentTitle, spaceKey, type },
	});
};

const queryContentById = (network: ResolverContext['network'], contentId: string) => {
	if (expValEquals('cc_migrate_experimental_macro_metadata_agg', 'isEnabled', true)) {
		return queryInterop<
			ExcerptIncludeTargetPageByIdQuery$data,
			ExcerptIncludeTargetPageByIdQuery$variables
		>({
			query: ExcerptIncludeTargetPageByIdQuery,
			variables: { contentId },
			interopApolloAggClient: true,
		}).then((result) => ({ data: result.data }));
	}
	return queryGraphQL(network, {
		query: `
      query excerptIncludeTargetPageQueryById(
        $contentId: ID!
      ) {
        content(id: $contentId) {
          nodes {
            id
            body {
              atlas_doc_format {
                value
              }
            }
          }
        }
      }
    `,
		variables: { contentId },
	});
};

const getSpaceKey = (network: ResolverContext['network'], contentId: string) => {
	if (expValEquals('cc_migrate_experimental_macro_metadata_agg', 'isEnabled', true)) {
		return queryInterop<ConsumerSpaceQuery$data, ConsumerSpaceQuery$variables>({
			query: ConsumerSpaceQuery,
			variables: { contentId },
			interopApolloAggClient: true,
		}).then((result) => ({ data: result.data }));
	}
	return queryGraphQL(network, {
		query: `
      query consumerSpaceQuery($contentId: ID!) {
        content(id: $contentId, status: ["current", "draft"]) {
          nodes {
            id
            space {
              key
            }
          }
        }
      }
    `,
		variables: { contentId },
	});
};

/**
 * Guesses at whether the given title is from a blog or not (unreliable as
 * shiiiiiiiit).
 *
 * TODO: Remove dis.
 *
 * @param contentTitle The title of the content
 */
const isPossiblyBlogpost = (contentTitle: string) =>
	contentTitle.match(/^\/\d{4}\/\d{2}\/\d{2}\//) !== null;

/**
 * Guesses at whether the given title is from an external space or not.
 *
 * TODO: Remove this.
 *
 * @param contentInfo The title of the content
 */
const isPossiblyExternalContent = (contentInfo: string) => contentInfo.indexOf(':') !== -1;

// Length of the date prefix that is in the data returned by the macro
const blogPrefixLength = '/yyyy/mm/dd/'.length;

/**
 * Removes the blog title prefix from the given content info.
 *
 * @param contentInfo The content info containing a blog title w/ prefix
 */
const getBlogpostTitle = (contentInfo: string) => contentInfo.slice(blogPrefixLength);

/**
 * For a content title that's prefixed with the space key, this strips the
 * space key out assuming that it matches the pattern `${SPACE_KEY}:${TITILE}`.
 *
 * @param contentInfo A content title assumedly prefixed with space key + ":"
 */
const getExternalContentTitle = (contentInfo: string) =>
	contentInfo.slice(contentInfo.indexOf(':') + 1);

/**
 * For a content title that's prefixed with the space key, this strips the
 * title out assuming that it matches the pattern `${SPACE_KEY}:${TITILE}`.
 *
 * @param contentInfo A content title assumedly prefixed with space key + ":"
 */
const getExternalSpaceKey = (contentInfo: string) => contentInfo.slice(0, contentInfo.indexOf(':'));

/**
 * Resolver for the `macroMetadata` query.
 *
 * TODO: This only works for "excerpt-include". This will need to be cleaned up
 * so that other macro's can return their payload as well. CFE-1812
 * TODO: Sub todo. This is shit. There needs to be a better API for querying
 * macros, or for macros to return better metadata (like contentId). CFE-1811
 *
 * @param args.contentId The content ID the macro belongs to
 * @param args.macroNode A simplified version of the macro node (no content,
 * and macroParams + macroMetadata are stringified)
 */
export const experimentalMacroMetadata: ExperimentalQueries['experimentalMacroMetadata'] = (
	_,
	args,
	{ network },
) => {
	const {
		contentId,
		macroNode: {
			extensionKey,
			parameters: { macroParams: macroParamsString },
		},
	} = args;

	// Handles what logic to do based on the macro's extension key
	switch (extensionKey) {
		case 'excerpt-include':
			const macroParams = JSON.parse(macroParamsString);
			const targetContentId = macroParams?._referencedContentId?.value ?? null;

			if (targetContentId !== null) {
				return handleExcerptIncludeByTargetId(targetContentId, macroParams, network);
			} else {
				return handleExcerptInclude(contentId, macroParams, network);
			}
		default:
			return Promise.resolve(null);
	}
};

const handleExcerptIncludeByTargetId = async (
	targetContentId: string,
	macroParams: any,
	network: ResolverContext['network'],
) => {
	const response = await queryContentById(network, targetContentId);
	const targetPageAdfString =
		response?.data?.content?.nodes[0]?.body?.atlas_doc_format?.value ?? null;

	return formatResponse(targetPageAdfString, targetContentId, macroParams);
};

/**
 * Handles the payload logic for the excerpt include macro.
 *
 * This shotguns four queries, and then uses the response of whatever sticks
 * first. That response contains an ADF node that we parse to find the first
 * instance of an excerpt macro. We then return that macros content
 * stringified.
 *
 * @param contentId The excerpt include macro's content ID
 * @param macroParams The params object from the excerpt macro
 * @param cfetch cfetch
 */
const handleExcerptInclude = (
	contentId: string,
	macroParams: any,
	network: ResolverContext['network'],
) =>
	// We need to get the space key of the excerpt include macro so we can check
	// if the excerpt macro is from the same space.
	getSpaceKey(network, contentId).then((consumerContextResponse) => {
		// This is either going to be of the pattern "${TITLE}",
		// "${SPACE_KEY}:${TITLE}", "/YYYY/MM/DD/${TITLE}",
		// "${SPACE_KEY}:/YYYY/MM/DD/${TITLE}". Unfortunately, we don't know
		// which we're getting, so we'll query them all.
		const targetPageInfo = macroParams?.['']?.value;
		const consumerSpaceKey = consumerContextResponse?.data?.content?.nodes?.[0]?.space?.key;

		// Just shotgun it, and grab one that sticks
		return Promise.all([
			// Page within current space "${TITLE}"
			queryContent(network, targetPageInfo, consumerSpaceKey, 'page'),
			// Blogpost within current space "/YYYY/MM/DD/${TITLE}"
			isPossiblyBlogpost(targetPageInfo)
				? queryContent(network, getBlogpostTitle(targetPageInfo), consumerSpaceKey, 'blogpost')
				: // Not possibly an blogpost
					Promise.resolve(null),
			// Page within outside space "${SPACE_KEY}:${TITLE}"
			isPossiblyExternalContent(targetPageInfo)
				? queryContent(
						network,
						getExternalContentTitle(targetPageInfo),
						getExternalSpaceKey(targetPageInfo),
						'page',
					)
				: // Not possibly an external page
					Promise.resolve(null),
			// Blogpost within outside space "${SPACE_KEY}:/YYYY/MM/DD/${TITLE}"
			isPossiblyExternalContent(targetPageInfo) &&
			isPossiblyBlogpost(getExternalContentTitle(targetPageInfo))
				? queryContent(
						network,
						getBlogpostTitle(getExternalContentTitle(targetPageInfo)),
						getExternalSpaceKey(targetPageInfo),
						'blogpost',
					)
				: // Not possibly an external blogpost
					Promise.resolve(null),
		]).then((responses) => {
			// Go through the response and get the first valid adf string, and the associated contentId
			let targetPageAdfString: any = null;
			let targetContentId: any = null;
			responses.some((response) => {
				targetPageAdfString =
					response?.data?.content?.nodes?.[0]?.body?.atlas_doc_format?.value || null;
				if (targetPageAdfString) {
					targetContentId = response?.data?.content?.nodes?.[0]?.id || null;
					return true;
				}
			});

			return formatResponse(targetPageAdfString, targetContentId, macroParams);
		});
	});

const formatResponse = (targetPageAdfString: any, targetContentId: any, macroParams: any) => {
	// If we didn't get anything, just return null
	if (!targetPageAdfString) {
		return {
			payload: null,
			contentId: null,
		};
	}

	const targetAdf = JSON.parse(targetPageAdfString);

	// If a excerpt name is given, then we try to find the excerpt with name, otherwise we grab the first excerpt
	const excerptName = macroParams?.['name']?.value;
	const targetNodes = parseForTargetMacroNodeByExtensionKey('excerpt', targetAdf);

	let targetNodeContent = targetNodes[0]?.content;
	if (excerptName) {
		targetNodeContent = targetNodes.find(
			(node: any) =>
				node?.attrs?.parameters?.macroParams['name']?.value?.toUpperCase?.() ===
				excerptName.toUpperCase(),
		)?.content;
	}

	const payload = targetNodeContent ? JSON.stringify(targetNodeContent) : null;

	return { payload, contentId: targetContentId };
};
